import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {
  AssayResultFieldValidation,
  AssayResultValidation,
  AssayValidationByResultId,
  AssayValidationComment,
  AssayValidationDisplayInfo,
  FieldHasValueDictByResultId,
  ReadableAssayValidationStatus,
} from './ValidationConstants';

const VALID = ReadableAssayValidationStatus.VALID;
const PARTIALLY_VALID = ReadableAssayValidationStatus.PARTIALLY_VALID;
const INVALID = ReadableAssayValidationStatus.INVALID;

function _getAggregateValidationComment(
  assayResultFieldValidations: Array<AssayResultFieldValidation>,
  aggregateStatus: ReadableAssayValidationStatus,
  locationCapitalized: string
): AssayValidationComment {
  const firstComment = assayResultFieldValidations.length ? assayResultFieldValidations[0].comment : null;
  let aggregateComment = assayResultFieldValidations.every(
    (fieldValidaton) => fieldValidaton.comment === firstComment
  )
    ? firstComment
    : null;

  if (!aggregateComment) {
    if (aggregateStatus !== VALID) {
      aggregateComment = `${locationCapitalized} contains invalidated data`;
    } else if (assayResultFieldValidations.some((validation) => _fieldValidationHasComment(validation))) {
      aggregateComment = `${locationCapitalized} contains comments`;
    }
  }
  return aggregateComment;
}

function _fieldIsArchived(fieldApiId: string, schemaFieldsByApiId: {[apiId: string]: SchemaField}): boolean {
  // If `fieldApiId` is not in `schemaFieldsByApiId`, that means the server knows about a field
  // which the client currently doesn't. This should only happen when a field was just added to
  // the schema (while the entry was open and then got saved), and therefore it would not be archived
  return fieldApiId in schemaFieldsByApiId && isSchemaFieldArchived(schemaFieldsByApiId[fieldApiId]);
}

export function getAggregateRowValidation(
  assayResultValidationForRow: AssayResultValidation,
  assaySchema: AssaySchema
): AssayValidationDisplayInfo {
  const aggregateStatus = assayResultValidationForRow.status;

  const schemaFieldsByApiId = _.indexBy(assaySchema.allFields, 'apiId');
  const notArchivedResultFieldValidations: Array<AssayResultFieldValidation> = Object.values(
    // eslint-disable-next-line no-restricted-syntax
    _.omit(assayResultValidationForRow.fieldValidation, (val: AssayResultValidation, fieldApiId: string) =>
      _fieldIsArchived(fieldApiId, schemaFieldsByApiId)
    )
  );
  return {
    status: aggregateStatus,
    comment:
      assayResultValidationForRow.comment ||
      _getAggregateValidationComment(notArchivedResultFieldValidations, aggregateStatus, 'Row'),
  };
}

function _fieldValidationStatusIsInvalid(fieldValidation: AssayResultFieldValidation): boolean {
  return fieldValidation.status === INVALID;
}

function _fieldValidationHasComment(fieldValidation: AssayResultFieldValidation): boolean {
  return Boolean(fieldValidation.comment);
}

interface AssayResultFieldValidationsByResult {
  [resultId: string]: {[schemaFieldId: string]: AssayResultFieldValidation};
}

/**
 * Implementation mirrors that of ResultValidationProxy::get_current() in
 * benchling/results/validation/get_set/result_validation_proxy.py, but with different rules for
 * aggregate comment
 */
export function getAggregateColumnValidation(
  assayValidationByResultIdForTable: AssayValidationByResultId,
  fieldHasValueDictByIdForTable: FieldHasValueDictByResultId,
  schemaFieldIdForColumn: string
): AssayValidationDisplayInfo {
  const hasValueByResultIdForColumn: {[resultId: string]: boolean} = _.mapObject(
    fieldHasValueDictByIdForTable,
    (schemaFieldDict) => schemaFieldDict[schemaFieldIdForColumn]
  );

  const fieldValidationsByResultId: AssayResultFieldValidationsByResult = _.mapObject(
    assayValidationByResultIdForTable,
    (assayResultValidation) => assayResultValidation.fieldValidation
  );
  // eslint-disable-next-line no-restricted-syntax
  const fieldValidationsForNonNullFields: AssayResultFieldValidationsByResult = _.omit(
    fieldValidationsByResultId,
    (val: {[schemaFieldId: string]: AssayResultFieldValidation}, resultId: string) =>
      !hasValueByResultIdForColumn[resultId]
  );

  // Guard against missing validation objects for this column's schema field.
  const missingFieldValidationsByResultId: AssayResultFieldValidationsByResult = {};
  for (const [resultId, schemaFieldIdToValidation] of Object.entries(fieldValidationsByResultId)) {
    if (schemaFieldIdToValidation[schemaFieldIdForColumn] == null) {
      missingFieldValidationsByResultId[resultId] = schemaFieldIdToValidation;
    }
  }
  if (Object.keys(missingFieldValidationsByResultId).length > 0) {
    assertAndContinue(
      // If any result is missing a validation object for this column's schema field,
      // it is most likely because the schema field was just added to the result schema,
      //
      // If this column's schema field was just added to the schema, we expect
      // all results in the table to be missing a validation object for the new field.
      // If not all results are missing a validation object, then
      // *we don't know the cause of the missing validation objects*.
      Object.keys(missingFieldValidationsByResultId).length ===
        Object.keys(fieldValidationsByResultId).length,
      // If we don't know the cause of the missing validation objects,
      // then log a warning so we can investigate.
      'Some results are missing validation objects for this field, but other results are not',
      {
        schemaFieldIdForColumn,
        fieldValidationsByResultId,
        missingFieldValidationsByResultId,
      }
    );
    // Some validation objects are missing, most likely because this column's schema field
    // was just added to the result schema. If the field was just added, all the field values
    // should be valid, so it's safe to return VALID as the aggregated column status.
    return {
      status: VALID,
      comment: null,
    };
  }

  const allFieldValidationsForCol: Array<AssayResultFieldValidation> = Object.values(
    fieldValidationsByResultId
  ).map((schemaFieldIdToValidation) => schemaFieldIdToValidation[schemaFieldIdForColumn]);
  const nonNullFieldValidationsForCol: Array<AssayResultFieldValidation> = Object.values(
    fieldValidationsForNonNullFields
  ).map((schemaFieldIdToValidation) => schemaFieldIdToValidation[schemaFieldIdForColumn]);

  let aggregateStatus;
  if (!allFieldValidationsForCol.some((validation) => _fieldValidationStatusIsInvalid(validation))) {
    aggregateStatus = VALID;
  } else if (
    nonNullFieldValidationsForCol.every((validation) => _fieldValidationStatusIsInvalid(validation))
  ) {
    aggregateStatus = INVALID;
  } else {
    aggregateStatus = PARTIALLY_VALID;
  }

  return {
    status: aggregateStatus,
    comment: _getAggregateValidationComment(nonNullFieldValidationsForCol, aggregateStatus, 'Column'),
  };
}
